import {
  IconButton,
  NormalButton,
  Chip,
  CustomButton,
  CustomTooltip,
} from '@/shared/ui/components';
import {
  shouldDisplayStudyStartTime,
  generateIssueFound,
  STUDY_STATUS,
  checkBiotresOfflineWarning,
  checkBiotresRefCodeWarning,
  isDisabledViewLiveEcgButton,
  shouldDisplayViewLiveEcgButton,
  checkUsingHighSamplingRateStudy,
  C1cDeviceLabel,
} from '@/entities/study';
import {
  AbortStudyIcon,
  LiveEcgIcon,
  PendingCommandIcon,
  Warning18pxIcon,
  copyIcon,
  ecgIcon,
  infoIconTransparent,
  whiteArrowBackIcon,
  whiteArrowNextIcon,
} from '@/shared/ui/assets';
import {
  BIOTRES_OFFLINE_WARNING_MESSAGE,
  DEVICE,
  DOUBLE_HYPHEN_BULLET,
  FLAGGED_REASON,
} from '@/shared/config';
import { DEVICE_STATUS, AppPaired, HighSamplingRate } from '@/entities/device';
import { getDuration, zeroPad, dateToString, formatInitialDuration } from '@/shared/lib';
import { Progress } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import _ from 'lodash';
import React, { forwardRef } from 'react';
import ImageGallery from 'react-image-gallery';
import Skeleton from 'react-loading-skeleton';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import StudyCardSkeleton from '../studyCardSkeleton/studyCardSkeleton';
import { MarkAddressedStudy } from '../../markAddressedStudy';
import '../style.scss';
import { isCheckCanStopStudy } from './handler';
import { useStudyCard } from './useStudyCard';
import BiodirectProgress from './biodirectProgress/biodirectProgress';
import type { Study, StudyCardProps } from './studyCard.types';
import './studyCard.scss';

dayjs.extend(utc);

/**
 * Renders one ongoing or flagged study as a card with device, study, patient and facility headers, an information block, progress and an ECG strip gallery. StudyCardSkeleton stands in until isLoadedData, then abort, copy, get-ECG, live-ECG and open-study actions are drawn per tab and device model, mark-addressed appears on the flagged tab, and an aborting or stopping alert replaces generateProgressComponent with buttonRenderer, while the strip gallery falls back to a no-strip message.
 *
 * @returns The study card element, or a skeleton while data loads.
 */
const StudyCard = forwardRef((props: StudyCardProps, ref) => {
  const {
    state,
    isBiodirectStudy,
    hasSentRefCode,
    isIcorDevice,
    isCardioOneFacilityStudy,
    isC1cProviderStudy,
    isManagedC1cDeviceStudy,
    onImageLoad,
    onImageError,
    getRemainingMinutes,
    isShowStoppingAlert,
    isShowAbortingAlert,
    isDisabledGetECGButton,
    createItems,
  } = useStudyCard(props, ref);

  /**
   * Renders the duration and upload progress block of a study card. DRAFT and STARTING studies get a not-started message; otherwise upload versus recording progress is decided from ecgUploadProgress for iCOR devices or from remaining minutes, device status, uploadStatus and lastStudyHistory, printing the uploaded percentage with the getDuration time left and an info button opening the upload-status modal, or the remaining duration text, over a Progress bar blue for uploads and green for recording.
   *
   * @param study - The study whose progress and remaining duration are rendered.
   * @returns The progress text and bar elements.
   */
  const generateProgressComponent = (study: Study) => {
    const { id, uploadStatus, lastStudyHistory, ecgUploadProgress, progress, device, status } =
      study || {};
    // Stop manual đang đợi connect với App để Upload Study
    const isCompletedIcorDevice = status === STUDY_STATUS.COMPLETED && isIcorDevice;

    const hasNotStartedStudy = [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING].includes(study.status);
    if (hasNotStartedStudy) {
      return (
        <div className="text-container">
          <span>{t`The study has not started yet`}</span>
        </div>
      );
    }

    const remainingMinutes = getRemainingMinutes(study);
    const isUploadProgress = isIcorDevice
      ? !!ecgUploadProgress
      : (remainingMinutes <= 0 && device.status !== DEVICE_STATUS.OFFLINE) ||
          !_.isEmpty(uploadStatus)
        ? true
        : lastStudyHistory
          ? [STUDY_STATUS.UPLOADING, STUDY_STATUS.UPLOADED].includes(lastStudyHistory.status)
          : false;
    const progressValue = isUploadProgress
      ? isIcorDevice
        ? ecgUploadProgress
        : !_.isEmpty(uploadStatus)
          ? uploadStatus.percentage
          : ecgUploadProgress
      : isCompletedIcorDevice
        ? 100
        : progress;
    const getDurationValue = getDuration(
      uploadStatus ? uploadStatus.aboutTimeLeft : remainingMinutes,
      'd[d] hh[h] mm[m]',
      'h',
      uploadStatus ? 'seconds' : 'minutes',
    );

    return (
      <>
        <div className="remaining-time">
          {isUploadProgress ? (
            isIcorDevice || _.isEmpty(uploadStatus) ? (
              <span>{`${t`Uploading`} ${progressValue}%`}</span>
            ) : (
              <>
                <span className="time-progress">{`${t`Total uploaded`}: ${progressValue}%`}</span>
                <div className="time-left">
                  <span>{`${t`About`} ${getDurationValue} ${t`left`}`}</span>
                  <IconButton
                    id={`biotres-progress-warning-icon-${id}`}
                    aria-label={t`Upload status info`}
                    className="biotres-progress-icon"
                    iconComponent={<img src={infoIconTransparent} alt="info icon" />}
                    onClick={() => {
                      props.toggleDisplayUploadStatusModal(study);
                    }}
                  />
                </div>
              </>
            )
          ) : isCompletedIcorDevice ? (
            <span>{t`Study duration ended`}</span>
          ) : (
            !_.isNaN(remainingMinutes) &&
            (remainingMinutes > 0 ? (
              <span>
                {`${getDuration(remainingMinutes, 'd[d] hh[h] mm[m]', 'h')} ${t`remaining`}`}
              </span>
            ) : (
              <span>{t`Study duration ended`}</span>
            ))
          )}
        </div>
        <Progress
          className="study-process"
          percent={progressValue}
          showInfo={false}
          strokeColor={isUploadProgress ? '#007DD6' : '#07A83D'}
          aria-label={t`Study progress`}
        />
      </>
    );
  };

  /**
   * Renders the pending-command banner of a study card. Pairs the pending-command icon with the supplied message text.
   *
   * @param alertMessage - The alert text shown beside the icon.
   * @returns The alert banner element.
   */
  const alertStudyRenderer = (alertMessage: string) => (
    <div className="aborting-study__container">
      <img className="aborting-study__icon" src={PendingCommandIcon} alt="Pending command icon" />
      <span className="aborting-study__message">{alertMessage}</span>
    </div>
  );

  /**
   * Picks the destructive action button of a study card. DRAFT and STARTING studies get Abort, disabled with a contact-support tooltip when a Biodirect study already has an order number or a real device id, and nothing when the external source provider is `c1c`; otherwise Stop appears while duration remains and no upload is running, guarded by isCheckCanStopStudy and labelled Stopping while the request is in flight, and nothing is rendered when neither case applies.
   *
   * @returns The abort or stop button, or null when no action is available.
   */
  const buttonRenderer = () => {
    // TODO: Stop is shown for c1c studies for this phase only; restore this guard next phase.
    // if (isC1cProviderStudy) {
    //   return null;
    // }

    const canAbortStudyStatuses = [STUDY_STATUS.DRAFT, STUDY_STATUS.STARTING];
    const shouldDisplayAbortButton = canAbortStudyStatuses.includes(props.study.status);
    const isRemaining = getRemainingMinutes(props.study) > 0;
    const isCompletedIcorDevice = props.study.status === STUDY_STATUS.COMPLETED && isIcorDevice;
    if (shouldDisplayAbortButton) {
      if (isC1cProviderStudy) {
        return null;
      }
      const isDisabledAbortButton = isBiodirectStudy
        ? props.study.orderNumber ||
          (props.study.device?.deviceId && props.study.device?.deviceId !== '0000000000')
        : false;
      return (
        <CustomButton
          id={`abort-study-btn-${props.study.id}`}
          type="outline"
          style="danger"
          size="small"
          onClick={props.handleAbortClick}
          disabled={isDisabledAbortButton}
          tooltipTitle={isDisabledAbortButton && t`Please contact support to abort this study`}
          tooltipPlacement="bottom"
        >
          {t`Abort`}
        </CustomButton>
      );
    }

    const shouldDisplayStopButton = isIcorDevice
      ? !props.study.ecgUploadProgress && isRemaining && !isCompletedIcorDevice
      : isRemaining && _.isEmpty(props.study.uploadStatus);
    if (shouldDisplayStopButton) {
      return (
        <CustomButton
          id={`stop-study-btn-${props.study.id}`}
          type="outline"
          style="danger"
          size="small"
          disabled={isCheckCanStopStudy(props.study)}
          onClick={props.handleClickStop}
          loading={props.study.isStopping}
          tooltipPlacement="bottom"
        >
          {props.study.isStopping ? t`Stopping` : t`Stop`}
        </CustomButton>
      );
    }

    return null;
  };

  /**
   * Renders the previous-strip arrow of the ECG strip gallery. Draws a button holding the white back arrow image, wired to the gallery's click handler and disabled flag.
   *
   * @param onClick - Gallery handler moving to the previous strip.
   * @param disabled - Whether the first strip is already shown.
   * @returns The left navigation button element.
   */
  const renderLeftNav = (
    onClick: React.MouseEventHandler<HTMLButtonElement>,
    disabled: boolean,
  ) => (
    <button
      type="button"
      aria-label={t`Previous`}
      className="image-gallery-custom-nav left"
      disabled={disabled}
      onClick={onClick}
    >
      <img src={whiteArrowBackIcon} alt="back" />
    </button>
  );

  /**
   * Renders the next-strip arrow of the ECG strip gallery. Draws a button holding the white next arrow image, wired to the gallery's click handler and disabled flag.
   *
   * @param onClick - Gallery handler moving to the next strip.
   * @param disabled - Whether the last strip is already shown.
   * @returns The right navigation button element.
   */
  const renderRightNav = (
    onClick: React.MouseEventHandler<HTMLButtonElement>,
    disabled: boolean,
  ) => (
    <button
      type="button"
      aria-label={t`Next`}
      className="image-gallery-custom-nav right"
      disabled={disabled}
      onClick={onClick}
    >
      <img src={whiteArrowNextIcon} alt="next" />
    </button>
  );

  /**
   * Renders the information block of a study card, artifact details on the flagged tab and study fields elsewhere. The flagged variant prints the pending-upload notice or the last and common issues from generateIssueFound plus the artifact percentage badged need-attention from 50% and potentially lost from 70%, with a view-solutions button; the standard variant lists patient ID, times, type with a follow-on chip, durations, return visit, last strip update and the reference code with its send button.
   *
   * @returns The study or artifact information block.
   */
  const studyInfoRenderer = () => {
    if (props.isFlaggedStudiesTab) {
      const { reason } = props.study.flaggedStudy || {};
      const isPendingUpload = reason === FLAGGED_REASON.PENDING_UPLOAD;
      const isPotentiallyLost = props.study.artifact?.percentage >= 70;
      const isNeedAttention = props.study.artifact?.percentage >= 50;
      return (
        <div className="artifact-info">
          {isPendingUpload ? (
            <div className="info-row">
              <div className="title">{`${t`Common issue found in study`}:`}</div>
              <span className="content">Data upload pending due to offline device</span>
            </div>
          ) : (
            <>
              <div className="info-row">
                <div className="title">{`${t`Issue found last 6 hours`}:`}</div>
                <span className="content">
                  {
                    generateIssueFound(
                      props.study.artifact?.lastIssueFound,
                      props.study.device?.model,
                    )?.issueFound
                  }
                </span>
              </div>
              <div className="info-row">
                <div className="title">{`${t`Common issue found in study`}:`}</div>
                <span className="content">
                  {
                    generateIssueFound(
                      props.study.artifact?.commonIssueFound,
                      props.study.device?.model,
                    )?.issueFound
                  }
                </span>
              </div>
              <div className="info-row">
                <div className="title">{`${t`Total artifact percentage`}:`}</div>
                {!_.isNil(props.study.artifact?.percentage) ? (
                  <div
                    className={classNames(
                      'total-artifact-percentage',
                      isPotentiallyLost
                        ? '--potentially-lost'
                        : isNeedAttention
                          ? '--need-attention'
                          : '',
                    )}
                  >
                    <strong>{`${props.study.artifact?.percentage}%`}</strong>
                    {(isPotentiallyLost || isNeedAttention) && (
                      <div className="label">
                        {isPotentiallyLost ? 'Potentially lost' : 'Need attention'}
                      </div>
                    )}
                  </div>
                ) : (
                  DOUBLE_HYPHEN_BULLET
                )}
              </div>
            </>
          )}

          <CustomButton
            className="view-solutions-btn"
            style="default"
            size="small"
            type="secondary"
            id="view-solutions-btn"
            onClick={() => {
              props.toggleDisplaySolutionsDrawer(props.study);
            }}
          >
            {t`View solutions`}
          </CustomButton>
          <div className="d-flex mt-2">
            {isBiodirectStudy && <div className="biodirect-label mr-2">{t`Biodirect`}</div>}
          </div>
        </div>
      );
    }

    const canNotViewReferenceCodeStudyStatus =
      props.study.device?.model === DEVICE.BIOFLUX
        ? [STUDY_STATUS.COMPLETED, STUDY_STATUS.DONE]
        : [STUDY_STATUS.DONE];
    return (
      <div className="study-info">
        <div className="info-col">
          <div className="title">{`${t`Patient ID`}:`}</div>
          <div className="title">{`${t`Initiated time`}:`}</div>
          <div className="title">{`${t`Start time`}:`}</div>
          <div className="title">{`${t`Study type`}:`}</div>
          <div className="title">{`${t`Study duration`}:`}</div>
          <div className="title">{`${t`Initial duration`}:`}</div>
          <div className="title">{`${t`Patient return visit`}:`}</div>
          <div className="title">{`${t`Last strip update`}:`}</div>
          <div className="title">{`${t`Reference code`}:`}</div>
        </div>
        <div className="info-col">
          <div>{props.study.patient?.patientId}</div>
          <div>{props.study.initiatedTime || DOUBLE_HYPHEN_BULLET}</div>
          <div>
            {shouldDisplayStudyStartTime(props.study.status)
              ? props.study.start
                ? dateToString({
                    startStudyDate: props.study.start,
                    isoDate: props.study.start,
                    timezone: props.study.timezone,
                    studyTimeOffset: props.study.studyTimeOffset,
                    facilityCountry: props.study.facility?.contact?.country,
                    facilityTimezone: props.study.facility?.timezone,
                  })
                : DOUBLE_HYPHEN_BULLET
              : DOUBLE_HYPHEN_BULLET}
          </div>
          <div style={{ display: 'flex' }}>
            {props.study.studyType}
            {(props.study.linkedStudies?.length > 0 || !_.isEmpty(props.study.followOnStudy)) && (
              <Chip
                style={{
                  marginLeft: 8,
                  border: 1,
                  paddingInline: 8,
                  fontWeight: 500,
                }}
                background="#DEF5EF"
                color="#045C45"
                closable={false}
              >
                {t`Follow-on`}
              </Chip>
            )}
          </div>
          <div>
            {props.study.duration
              ? getDuration(props.study.duration, 'd[d] hh[h] mm[m]')
              : DOUBLE_HYPHEN_BULLET}
          </div>
          <div>
            {props.study.initialDurationOfStudy
              ? formatInitialDuration(props.study.initialDurationOfStudy)
              : DOUBLE_HYPHEN_BULLET}
          </div>
          <div>
            {dateToString({
              isoDate: !_.isEmpty(props.study.followOnStudy)
                ? dayjs.utc(props.study.followOnStudy?.reportDueDate, 'YYYY-MM-DD') // *: YYYY-MM-DD string
                : props.study.reportDueDate, // *: ISO string
              timezone: 'UTC+0',
              format: 'MM/DD/YYYY',
            }) || DOUBLE_HYPHEN_BULLET}
          </div>
          <div>
            {dateToString({
              startStudyDate: props.study.start,
              isoDate: props.study.lastEvaluationEvent?.createdAt,
              timezone: props.study.timezone,
              facilityCountry: props.study.facility?.contact?.country,
              facilityTimezone: props.study.facility?.timezone,
            }) || DOUBLE_HYPHEN_BULLET}
          </div>
          {isIcorDevice || isManagedC1cDeviceStudy ? (
            <div className="reference-code">{DOUBLE_HYPHEN_BULLET}</div>
          ) : (
            <div className="reference-code">
              {!canNotViewReferenceCodeStudyStatus.includes(props.study.status) &&
              props.study.status !== STUDY_STATUS.ABORTED ? (
                <>
                  {props.study.referenceCode ?? DOUBLE_HYPHEN_BULLET}
                  {!!props.study.referenceCode &&
                    !!props.study.device?.deviceId &&
                    props.study.device?.deviceId !== '0000000000' &&
                    props.study.status !== STUDY_STATUS.DRAFT && (
                      <NormalButton
                        id={`resend-id-btn-${props.study.id}`}
                        className="resend-btn"
                        buttonName={hasSentRefCode ? t`Resend` : t`Send`}
                        isText
                        onClick={() => {
                          props.toggleDisplayResendModal(props.study);
                        }}
                      />
                    )}
                </>
              ) : (
                DOUBLE_HYPHEN_BULLET
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Renders the reference-code disclaimer icon of a Biotres study card. It appears only when checkBiotresRefCodeWarning passes, as a tooltipped warning icon whose click opens the disclaimer modal for the study.
   *
   * @returns The warning icon, or false when no disclaimer applies.
   */
  const renderBiotresRefCodeWarningIcon = () =>
    checkBiotresRefCodeWarning(props.study) && (
      <CustomTooltip title={t`Disclaimer, click to learn more`} placement="bottom">
        <div>
          <IconButton
            id={`biotres-ref-code-warning-icon-${props.study.id}`}
            aria-label={t`Disclaimer, click to learn more`}
            className="biotres-warning-icon"
            iconComponent={<img src={Warning18pxIcon} alt="Warning 18px icon" />}
            onClick={() => {
              props.toggleDisplayDisclaimerModal(props.study);
            }}
          />
        </div>
      </CustomTooltip>
    );

  /**
   * Renders the offline warning icon of a Biotres study card. It appears only when checkBiotresOfflineWarning passes for the study, as a non-interactive warning icon inside a tooltip carrying BIOTRES_OFFLINE_WARNING_MESSAGE.
   *
   * @returns The warning icon, or false when no offline warning applies.
   */
  const renderBiotresOfflineWarningIcon = () =>
    checkBiotresOfflineWarning(props.study) && (
      <CustomTooltip placement="bottom" title={BIOTRES_OFFLINE_WARNING_MESSAGE}>
        <div>
          <IconButton
            id={`biotres-offline-warning-icon-${props.study.id}`}
            aria-label={t`Biotres offline warning`}
            className="biotres-warning-icon"
            iconComponent={<img src={Warning18pxIcon} alt="Warning 18px icon" />}
          />
        </div>
      </CustomTooltip>
    );

  if (!props.isLoadedData) {
    return <StudyCardSkeleton />;
  }

  return (
    <div className="study-card">
      <div className="header-container">
        <div className="header-container__left">
          <div
            className={classNames(
              'device-status',
              props.study.device?.deviceId === '0000000000' || isIcorDevice
                ? '--empty'
                : props.study.device?.status
                  ? props.study.device.status === DEVICE_STATUS.OFFLINE
                    ? '--gray'
                    : '--green'
                  : '',
            )}
          />
          <div className="title-block">
            <span className="title">{t`Device`}</span>
            <div className="flex">
              {props.study.device?.deviceId === '0000000000' ? (
                <span>{DOUBLE_HYPHEN_BULLET}</span>
              ) : isIcorDevice ? (
                <div className="icor-device-container">
                  <span className="icor-device-id">{props.study.device?.deviceId}</span>
                  <span className="icor-device-label">{DEVICE.ICOR}</span>
                </div>
              ) : isManagedC1cDeviceStudy ? (
                <span>{props.study.device?.deviceId}</span>
              ) : (
                <NormalButton
                  id={`device-id-study-btn-${props.study.id}`}
                  isText
                  isBold
                  buttonName={props.study.device?.deviceId}
                  onClick={props.handleClickDeviceId}
                  contentClassName="content-btn"
                />
              )}
              <C1cDeviceLabel deviceType={props.study.deviceType} className="ml8px" />
              {checkUsingHighSamplingRateStudy(props.study.ecgDetail) && (
                <HighSamplingRate hasTooltip />
              )}
              {!!props.study.patientFirstSyncDeviceAt && <AppPaired hasTooltip />}
              {renderBiotresOfflineWarningIcon()}
            </div>
          </div>
          <div className="title-block">
            <span className="title">{t`Study`}</span>
            <div className="flex">
              {!_.isNil(props.study.studyFid) ? (
                <NormalButton
                  id={`study-fid-btn-${props.study.id}`}
                  isText
                  isBold
                  buttonName={zeroPad(props.study.studyFid)}
                  onClick={props.handleClickStudyId}
                  contentClassName="content-btn"
                />
              ) : (
                <span>{DOUBLE_HYPHEN_BULLET}</span>
              )}
              {renderBiotresRefCodeWarningIcon()}
            </div>
          </div>
          <div className="title-block">
            <span className="title">{t`Patient`}</span>
            <NormalButton
              id={`patient-name-study-btn-${props.study.id}`}
              isText
              isBold
              buttonName={props.study.patient?.name}
              onClick={props.handleClickPatientName}
              contentClassName="content-btn"
            />
          </div>
          {props.isShowFacilityName && (
            <div className="title-block">
              <span className="title">{t`Facility`}</span>
              <span className="facility-name">{props.study.facility.name}</span>
            </div>
          )}
        </div>
        <div className="header-container__right">
          {props.isOngoing &&
            isIcorDevice &&
            !props.study.ecgUploadProgress &&
            !isC1cProviderStudy && (
              <CustomButton
                id="abort-study-btn"
                type="outline"
                style="danger"
                size="small"
                icon={<ReactSVG src={AbortStudyIcon} />}
                tooltipTitle={t`Abort study`}
                tooltipPlacement="bottom"
                onClick={props.handleAbortClick}
                ariaLabel={t`Abort study`}
              />
            )}
          {props.isOngoing && !isCardioOneFacilityStudy && (
            <CustomButton
              type="outline"
              id={`copy-click-btn-${props.study.id}`}
              className="ml3"
              onClick={props.handleClickCopy}
              tooltipTitle={t`Copy study`}
              size="small"
              tooltipPlacement="bottom"
              icon={<ReactSVG src={copyIcon} />}
              ariaLabel={t`Copy study`}
            />
          )}
          {props.isFlaggedStudiesTab && (
            <div className="resolved ml3">
              <MarkAddressedStudy
                lastAddressedTime={props.study.lastAddressedTime}
                onUpdateMarkAddressed={props.onUpdateMarkAddressed}
                studyId={props.study.id}
              />
            </div>
          )}
          {!isIcorDevice && !isManagedC1cDeviceStudy && (
            <CustomButton
              type="outline"
              id={`get-ecg-btn-${props.study.id}`}
              className="ml3"
              disabled={isDisabledGetECGButton(props.study)}
              onClick={props.handleGetEcgClick}
              tooltipTitle={!props.isFlaggedStudiesTab ? t`Get ECG` : undefined}
              ariaLabel={!props.isFlaggedStudiesTab ? t`Get ECG` : undefined}
              size="small"
              tooltipPlacement="bottom"
              icon={!props.isFlaggedStudiesTab ? <ReactSVG src={ecgIcon} /> : undefined}
            >
              {props.isFlaggedStudiesTab ? 'Get ECG' : undefined}
            </CustomButton>
          )}
          {props.isOngoing &&
            !isIcorDevice &&
            !isManagedC1cDeviceStudy &&
            shouldDisplayViewLiveEcgButton(props.study) && (
              <CustomButton
                type="outline"
                id={`live-ecg-btn-${props.study.id}`}
                className="ml3"
                disabled={isDisabledViewLiveEcgButton(props.study)?.disabled}
                onClick={() => {
                  props.displayViewLiveEcg(true, props.study);
                }}
                {...({
                  onCancel: () => {
                    props.displayViewLiveEcg(false);
                  },
                } as any)}
                tooltipTitle={
                  isDisabledViewLiveEcgButton(props.study)?.disabled
                    ? isDisabledViewLiveEcgButton(props.study)?.disabledText
                    : t`View live ECG`
                }
                size="small"
                tooltipPlacement="bottom"
                icon={<ReactSVG src={LiveEcgIcon} />}
                ariaLabel={t`View live ECG`}
              />
            )}
          {props.isOngoing && (
            <CustomButton
              size="small"
              type="outline"
              id={`view-study-btn-${props.study.id}`}
              className="ml3"
              onClick={props.handleGoToStudyManagement}
              tooltipTitle={t`Open study management`}
              tooltipPlacement="bottom"
            >
              {t`Open study`}
            </CustomButton>
          )}
        </div>
      </div>
      <div className="body-container">
        <div className="body-container__left">
          {studyInfoRenderer()}
          {!props.isFlaggedStudiesTab && (
            <BiodirectProgress
              study={props.study}
              onViewBiodirectInfoClick={props.handleViewBiodirectInfoClick}
            />
          )}
          {isShowAbortingAlert ? (
            alertStudyRenderer(
              t`Aborting study. Please ensure your device is online to complete the process.`,
            )
          ) : isShowStoppingAlert ? (
            alertStudyRenderer(
              t`Stopping study. Please ensure your device is online to complete the process.`,
            )
          ) : (
            <div className="study-duration">
              <div className="progress-container">{generateProgressComponent(props.study)}</div>
              {buttonRenderer()}
            </div>
          )}
        </div>
        <div className="body-container__right">
          {props.study.lastEvaluationEvent?.stripImgs?.length ? (
            <>
              <ImageGallery
                items={createItems(props.study.lastEvaluationEvent.stripImgs)}
                infinite={false}
                showPlayButton={false}
                showFullscreenButton={false}
                showBullets
                lazyLoad
                disableKeyDown
                disableThumbnailScroll
                slideDuration={250}
                startIndex={0}
                thumbnailPosition="bottom"
                renderLeftNav={renderLeftNav}
                renderRightNav={renderRightNav}
                onImageLoad={onImageLoad}
                onImageError={onImageError}
              />
              {state.isLoading ? (
                <div className="loading-thumbnail">
                  <Skeleton height="100%" />
                </div>
              ) : (
                state.isLoadedImageError && (
                  <div className="loading-thumbnail --error">
                    {t`There is an error while loading ECG strips`}
                  </div>
                )
              )}
            </>
          ) : (
            <div className="no-ecg-strip-container">
              <span className="no-ecg-strip-text">
                {isIcorDevice
                  ? t`Preview strip is not available for studies running on the iCOR device.`
                  : t`There is no strip to display`}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default StudyCard;
